import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useChatStore } from "@/hooks/useChatStore";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import {
  UPLOADING_STATE,
  type ChatStoreInstance,
  type PendingAttachments,
  type PendingSendReadiness,
} from "@/stores/chat/ChatStore";
import {
  ChatMessageTooLongError,
  ChatSendRejectedError,
  isChatMessageTooLong,
} from "@/stores/chat/messageLimits";

export interface UseChatSendParams {
  buildingId: string;
  orgId?: string | null;
  userIdentifier: string | null;
  threadId: string | null;
  /** Sessions/thread still settling — sending now would target the wrong thread. */
  isSessionBootstrapPending: boolean;
  /** Starts the typewriter animation when the backend generated a title. */
  onTitleGenerated: (animation: {
    sessionId: string;
    fullTitle: string;
    displayedChars: number;
  }) => void;
}

/**
 * Why a send never reached the thread. The thread has nothing to show for it —
 * no bubble was added — so the composer says it instead.
 * - `tooLong`: the typed text is over `MAX_CHAT_MESSAGE_LENGTH`.
 * - `startFailed`: the building's first chat session could not be created.
 * - `notSent`: the store refused the send (e.g. the session is signed out).
 * - `uploadFailed`: a file attached to the message failed to upload.
 */
export type ChatSendError = "tooLong" | "startFailed" | "notSent" | "uploadFailed";

/** How one dispatch ended. */
type SendOutcome =
  /** Dispatched — or parked behind the running turn. Either way it is on its way. */
  | { status: "sent" | "queued" }
  /** Refused before the thread was touched: the text is still the user's. */
  | { status: "rejected"; error: ChatSendError }
  /** Reached the thread and failed there; the thread carries the error bubble. */
  | { status: "failed" };

/**
 * Sending a message: session auto-creation, attachment headers, submit, and the
 * two variants around it (stop the stream, re-send an edited last message).
 *
 * Also owns `pillDraft` — text typed into the collapsed pill that the panel's
 * input adopts when it opens — because sending is what consumes it.
 *
 * The draft is only ever cleared once a send is accepted (dispatched or
 * queued), and every send that does not go through hands its text back to the
 * composer — from the composer itself, the queue, an edit or an answer card
 * alike. A refusal the thread cannot show is reported through `sendError`.
 */
export function useChatSend(params: UseChatSendParams) {
  const { buildingId, orgId, userIdentifier, threadId, isSessionBootstrapPending } = params;
  const chatStore = useChatStore();
  const { t } = useLanguage();
  const [pillDraft, setPillDraft] = useState("");
  const [sendError, setSendError] = useState<ChatSendError | null>(null);

  // Tracked here (rather than passed in) so the queue and the send path read
  // the same fact. The calling component is an observer, so this stays live.
  // `isThreadBusy`, not `getThreadIsLoading`: a send deferred behind an
  // attachment that is still extracting owns the thread just as much as a
  // stream does, and dispatching around it reorders the conversation.
  const isThreadBusy = threadId ? chatStore.isThreadBusy(threadId) : false;

  /**
   * Text the user still owns goes back to the composer, oldest first. A text
   * the composer already holds is left alone: a send refused straight from the
   * composer never left it, and pasting it in a second time would double it.
   */
  const restoreToComposer = useCallback((texts: string[]) => {
    if (texts.length === 0) return;
    setPillDraft((draft) => {
      const missing = texts.filter((text) => text.trim() !== draft.trim());
      return [...missing, draft].filter(Boolean).join("\n\n");
    });
  }, []);

  /**
   * Hand a whole run of messages back at once, oldest first, with one reason.
   * Calling `settleSend` per message would reverse them: each restore puts its
   * text ahead of the draft the previous one just left.
   */
  const rejectSends = useCallback(
    (texts: string[], error: ChatSendError) => {
      setSendError(error);
      restoreToComposer(texts);
    },
    [restoreToComposer]
  );

  /** The user editing the draft is their answer to whatever was reported. */
  const handleDraftChange = useCallback((value: string) => {
    setPillDraft(value);
    setSendError(null);
  }, []);

  const dismissSendError = useCallback(() => setSendError(null), []);

  /**
   * Resolve a finished dispatch into "was it accepted?", handing the text back
   * whenever it was not. Resolves true for dispatched or queued.
   */
  const settleSend = useCallback(
    (text: string, outcome: SendOutcome): boolean => {
      if (outcome.status === "sent" || outcome.status === "queued") return true;
      if (outcome.status === "rejected") setSendError(outcome.error);
      restoreToComposer([text]);
      return false;
    },
    [restoreToComposer]
  );

  const dispatchSend = useDispatchSend(chatStore, params);

  /**
   * Only the text actually being sent leaves the composer: tapping an example
   * question, answering a nudge or draining the queue must not wipe what the
   * user is half-way through typing.
   */
  const clearDraftMatching = useCallback((text: string) => {
    const trimmed = text.trim();
    setPillDraft((draft) => (draft.trim() === trimmed ? "" : draft));
  }, []);

  /**
   * Resolves true when the message was dispatched (or parked in the queue),
   * false when it was not — in which case its text is back in the composer.
   */
  const handleSendMessage = useCallback(
    (message: string, fromSuggestion = false): Promise<boolean> => {
      const trimmed = message.trim();
      if (!trimmed) return Promise.resolve(false);
      // Nowhere to send it yet. Not every caller is the composer (an edit, an
      // answer card), so the text still goes back to where it can be retried.
      if (!orgId || !userIdentifier || isSessionBootstrapPending) {
        return Promise.resolve(settleSend(trimmed, { status: "rejected", error: "notSent" }));
      }
      // Refused before the draft is touched: an over-long message must stay
      // exactly where the user can shorten it.
      if (isChatMessageTooLong(trimmed)) {
        return Promise.resolve(settleSend(trimmed, { status: "rejected", error: "tooLong" }));
      }
      setSendError(null);
      clearDraftMatching(trimmed);
      return dispatchSend(trimmed, { fromSuggestion }).then((outcome) =>
        settleSend(trimmed, outcome)
      );
    },
    [clearDraftMatching, dispatchSend, isSessionBootstrapPending, orgId, settleSend, userIdentifier]
  );

  useQueuedSendFlush({
    chatStore,
    threadId,
    paused: !orgId || !userIdentifier || isSessionBootstrapPending || isThreadBusy,
    readiness: chatStore.getPendingSendReadiness(threadId),
    dispatchSend,
    clearDraftMatching,
    settleSend,
    rejectSends,
  });

  // Stop stream handler (WHA-2637)
  const handleStopStream = useCallback(() => {
    if (!threadId) return;
    // Stop means "I want to take over", so anything queued behind this turn
    // must not fire the moment the stream ends. It goes back to the composer
    // rather than being binned — the user typed it, it is theirs, and so are
    // the files they attached to it.
    const pending = chatStore.takePendingSends(threadId);
    for (const entry of pending) chatStore.restoreComposerAttachments(entry.attachments);
    restoreToComposer(pending.map((entry) => entry.text));
    chatStore.stopStream(threadId);
  }, [chatStore, restoreToComposer, threadId]);

  /**
   * Drop one queued message — the ✕ on its pending bubble. By id, not by
   * position: a flush landing between render and click shifts every index.
   */
  const handleCancelQueued = useCallback(
    (id: string) => {
      if (!threadId) return;
      chatStore.removePendingSend(threadId, id);
    },
    [chatStore, threadId]
  );

  const editFailedCopy = t.documents.chatSidebar.editFailed;
  const handleEditLastMessage = useCallback(
    async (editedText: string) => {
      // Refuse an over-long edit BEFORE dropping the exchange it replaces: once
      // that is deleted server-side there is nothing to go back to.
      if (isChatMessageTooLong(editedText)) {
        void handleSendMessage(editedText);
        return;
      }
      if (!(await dropLastExchange(chatStore, buildingId, orgId, editFailedCopy))) return;
      void handleSendMessage(editedText);
    },
    [buildingId, chatStore, editFailedCopy, handleSendMessage, orgId]
  );

  return {
    pillDraft,
    setPillDraft,
    handleDraftChange,
    sendError,
    dismissSendError,
    handleSendMessage,
    handleStopStream,
    handleCancelQueued,
    handleEditLastMessage,
  };
}

/**
 * The send itself, bound to the current scope. Deliberately free of composer
 * state: the queue flush dispatches through this, and draining the queue must
 * not touch what the user is half-way through typing.
 */
function useDispatchSend(chatStore: ChatStoreInstance, params: UseChatSendParams) {
  const { buildingId, orgId, userIdentifier, isSessionBootstrapPending } = params;
  const { onTitleGenerated } = params;
  return useCallback(
    (message: string, options?: DispatchOptions): Promise<SendOutcome> => {
      if (!orgId || !userIdentifier || isSessionBootstrapPending) {
        return Promise.resolve({ status: "rejected", error: "notSent" });
      }
      const trimmed = message.trim();
      if (!trimmed) return Promise.resolve({ status: "rejected", error: "notSent" });
      const scope = { chatStore, buildingId, orgId, userIdentifier, onTitleGenerated };
      return dispatchMessage(scope, trimmed, options);
    },
    [buildingId, chatStore, isSessionBootstrapPending, onTitleGenerated, orgId, userIdentifier]
  );
}

interface QueuedSendFlushParams {
  chatStore: ChatStoreInstance;
  threadId: string | null;
  /** Any reason a queued message must not go out right now. */
  paused: boolean;
  /** Whether the head of the queue has every upload it carries. */
  readiness: PendingSendReadiness;
  dispatchSend: (message: string, options?: DispatchOptions) => Promise<SendOutcome>;
  /** Clears the composer when it holds the very text that just went out. */
  clearDraftMatching: (text: string) => void;
  /** Hands a send that did not go through back to the composer. */
  settleSend: (text: string, outcome: SendOutcome) => boolean;
  /** Hands several messages back together, oldest first, with one reason. */
  rejectSends: (texts: string[], error: ChatSendError) => void;
}

/**
 * Drain the queue whenever the thread is idle. There is no edge to detect:
 * a message only ever lands in the queue while a turn is running, so "idle
 * and something queued" is exactly the moment to send it — which also
 * recovers a queue left behind by a thread the user navigated away from.
 * The ref keeps a second dispatch out while the first is still in flight.
 */
function useQueuedSendFlush({
  chatStore,
  threadId,
  paused,
  readiness,
  dispatchSend,
  clearDraftMatching,
  settleSend,
  rejectSends,
}: QueuedSendFlushParams) {
  const flushingRef = useRef(false);
  useEffect(() => {
    if (!threadId || paused || flushingRef.current) return;
    // Peek, don't dequeue: the bubble has to outlive a send that bails (no
    // session, submit threw), or the message disappears with nothing sent.
    const next = chatStore.peekPendingSend(threadId);
    if (!next) return;
    // A message never goes out ahead of a file it was sent with.
    if (readiness === "uploading") return;
    if (readiness === "uploadFailed") {
      // Nor without it: the text and the files that did upload go back to the
      // composer, and the user decides whether to send without the failed one.
      // The WHOLE queue comes back, not just the head — anything parked behind
      // it is ready to go, and draining it here would put the user's later
      // message in the thread ahead of the one they are being handed back, so
      // the conversation reads in an order they never typed. They come back
      // oldest first, as Stop hands a queue back.
      const parked = chatStore.takePendingSends(threadId);
      for (const entry of parked) chatStore.restoreComposerAttachments(entry.attachments);
      rejectSends(
        parked.map((entry) => entry.text),
        "uploadFailed"
      );
      return;
    }
    flushingRef.current = true;
    // Cleared at dispatch, exactly as a directly-typed send does it, so a user
    // who re-typed the parked text while it waited sees the composer empty when
    // it goes out rather than when the answer comes back.
    clearDraftMatching(next.text);
    void dispatchSend(next.text, {
      flushing: true,
      attachments: next.attachments,
      fromSuggestion: next.fromSuggestion,
    })
      .then((outcome) => {
        chatStore.removePendingSend(threadId, next.id);
        // A failed send goes back to the composer (with its reason, when the
        // thread has none to show) rather than leaving a bubble that will
        // never fire — or retrying it, which the loading flag flipping back
        // would loop.
        settleSend(next.text, outcome);
      })
      .finally(() => {
        flushingRef.current = false;
      });
  }, [
    chatStore,
    clearDraftMatching,
    dispatchSend,
    paused,
    readiness,
    rejectSends,
    settleSend,
    threadId,
  ]);
}

/**
 * How this dispatch was reached. `flushing` is the queue draining — it must not re-queue.
 * `fromSuggestion` is carried rather than read at the call site because the message that
 * goes out may be one parked minutes earlier, and it is what `assistant_message_sent`
 * reports.
 */
interface DispatchOptions {
  /** The queue is sending a parked message; it must not be parked again. */
  flushing?: boolean;
  fromSuggestion?: boolean;
  /** The chips the parked message was sent with. */
  attachments?: PendingAttachments;
}

/** The scope one message is sent into. */
interface SendScope {
  chatStore: ChatStoreInstance;
  buildingId: string;
  orgId: string;
  userIdentifier: string;
  onTitleGenerated: UseChatSendParams["onTitleGenerated"];
}

/**
 * Expand the panel, park the message if a turn is still running, otherwise
 * submit it.
 */
async function dispatchMessage(
  scope: SendScope,
  trimmed: string,
  options?: DispatchOptions
): Promise<SendOutcome> {
  const { chatStore, buildingId, orgId, userIdentifier, onTitleGenerated } = scope;

  // If collapsed, expand first so user sees the response
  if (chatStore.isCollapsed) {
    chatStore.setCollapsed(false);
  }

  // Every refusal hands a parked message's files back with its text: the
  // composer gets the text through `settleSend`, and the chips left the
  // composer when the message was parked, so nothing else would return them.
  const reject = (error: ChatSendError): SendOutcome => {
    if (options?.attachments) chatStore.restoreComposerAttachments(options.attachments);
    return { status: "rejected", error };
  };

  // Checked before queueing, too: a parked message has to be one that can go.
  if (isChatMessageTooLong(trimmed)) return reject("tooLong");

  // Resolve the target BEFORE reading the composer — a first message awaits a
  // session being created, and everything that decides park-vs-send has to be
  // read after that await, from one snapshot. Reading the composer's chips
  // afterwards while gating on them beforehand sent a file picked during the
  // await with its `upload-N` placeholder id, which neither the backend nor
  // the queue can ever resolve.
  const target = await resolveSendTarget(chatStore, buildingId, orgId, userIdentifier);
  if (!target) return reject("startFailed");
  const { sessionId, effectiveThreadId } = target;
  chatStore.ensureThread({ threadId: effectiveThreadId, buildingId, orgId });

  // Prepend attached document + issue context if any. Headers are
  // emitted in the order docs → issues → user question so the agent
  // sees both kinds of attachments before the question (WHA-3336).
  // A parked message sends the chips it was parked with; a direct send takes
  // the composer's, and hands them back if the send is refused. Taken before
  // the park decision below so that decision is made about the very chips the
  // message would carry.
  const attachments = options?.attachments ?? chatStore.takeComposerAttachments();

  // A turn already running owns the thread, and a file still uploading has no
  // document id to send yet. Either way the message is parked — with its
  // chips, which have left the composer so they cannot be left behind or
  // picked up by a later message. `useQueuedSendFlush` dispatches it when the
  // stream ends and its uploads have landed; `flushing` is that dispatch, and
  // must not re-queue.
  // A message already waiting keeps its place: nothing is sent around it.
  const busy = chatStore.isThreadBusy(effectiveThreadId);
  const waiting = chatStore.getPendingSends(effectiveThreadId).length > 0;
  const uploading = attachments.documents.some((doc) => doc.state === UPLOADING_STATE);
  if (!options?.flushing && (busy || waiting || uploading)) {
    chatStore.enqueuePendingSend(
      effectiveThreadId,
      trimmed,
      attachments,
      options?.fromSuggestion ?? false
    );
    return { status: "queued" };
  }

  const attached = attachments.documents;
  const attachedSourceFilenames = attached.map((d) => d.filename);
  const finalMessage = buildMessageWithAttachments(trimmed, attached, attachments.issues);

  try {
    // WHA-3330: submitMessage either dispatches immediately (no
    // pending attachments) or queues for deferred dispatch (one or
    // more chips still extracting). Either way the user message is
    // added to the thread optimistically. The title-animation block
    // only fires for immediate dispatches; queued sends complete via
    // flushQueuedSend with no UI thread back here, so the typewriter
    // animation is skipped for those (acceptable — generated titles
    // are usually for first messages, not file-attached follow-ups).
    const outcome = await chatStore.submitMessage({
      threadId: effectiveThreadId,
      message: finalMessage,
      // The length limit counts what the user typed, not the headers above.
      userText: trimmed,
      userId: userIdentifier,
      attachedSourceFilenames,
      attachedDocuments: attached.map((d) => ({ id: d.id, state: d.state })),
      analytics: {
        suggestionUsed: options?.fromSuggestion ?? false,
        attachmentCount: attached.length,
      },
    });
    recordSend(chatStore, sessionId, outcome, onTitleGenerated);
    return { status: "sent" };
  } catch (error) {
    // A `ChatSendRejectedError` was thrown before the store added anything:
    // no user bubble, no error bubble. The thread cannot show it, so the
    // caller keeps the text and reports the refusal next to the composer.
    if (error instanceof ChatSendRejectedError) {
      chatStore.restoreComposerAttachments(attachments);
      const reason = error instanceof ChatMessageTooLongError ? "tooLong" : "notSent";
      return { status: "rejected", error: reason };
    }
    // Anything else failed after the optimistic add, and
    // ChatStore.sendMessage has appended an error bubble under the message.
    return { status: "failed" };
  }
}

/**
 * Remove the last user+assistant exchange so an edited message can replace it.
 * `false` means nothing was removed and nothing should be re-sent.
 */
async function dropLastExchange(
  chatStore: ChatStoreInstance,
  buildingId: string,
  orgId: string | null | undefined,
  failedCopy: { title: string; description: string }
): Promise<boolean> {
  if (!orgId || !buildingId) return false;
  const activeSession = chatStore.getActiveSession(buildingId);
  const sessionId = activeSession?.id;
  const sessionThreadId = activeSession?.threadId;
  if (!sessionId || !sessionThreadId) return false;

  try {
    await chatStore.deleteLastExchange({ sessionId, threadId: sessionThreadId });
    return true;
  } catch {
    toast.error(failedCopy.title, { description: failedCopy.description });
    return false;
  }
}

/**
 * Bookkeeping after a send lands: bump the session, and start the typewriter
 * when the backend named the conversation for us.
 */
function recordSend(
  chatStore: ChatStoreInstance,
  sessionId: string | undefined,
  outcome: { queued?: boolean; result?: { generatedTitle?: string } },
  onTitleGenerated: UseChatSendParams["onTitleGenerated"]
): void {
  if (!sessionId) return;
  chatStore.updateSessionAfterMessage(sessionId);
  if (outcome.queued || !outcome.result?.generatedTitle) return;
  chatStore.setSessionTitle(sessionId, "");
  onTitleGenerated({
    sessionId,
    fullTitle: outcome.result.generatedTitle,
    displayedChars: 0,
  });
}

/**
 * Which session and thread this message goes to, auto-creating the building's
 * first session when there is none. `null` means the send can't proceed; the
 * caller reports it next to the composer, where the text is handed back.
 */
async function resolveSendTarget(
  chatStore: ChatStoreInstance,
  buildingId: string,
  orgId: string,
  userIdentifier: string
): Promise<{ sessionId?: string; effectiveThreadId: string } | null> {
  // Ensure we always use the latest session state from the store
  const activeSession = chatStore.getActiveSession(buildingId);
  const fallbackThreadId = `${orgId}-${userIdentifier}-${buildingId}`;

  if (activeSession?.id) {
    return {
      sessionId: activeSession.id,
      effectiveThreadId: activeSession.threadId ?? fallbackThreadId,
    };
  }
  if (chatStore.getSessionsForBuilding(buildingId).length > 0) {
    return { effectiveThreadId: fallbackThreadId };
  }

  // Auto-create on first message
  try {
    const newSession = await chatStore.createSession({ orgId, buildingId });
    return {
      sessionId: newSession.id,
      effectiveThreadId: newSession.threadId ?? fallbackThreadId,
    };
  } catch (error) {
    console.error("Failed to create session:", error);
    return null;
  }
}

/** Attachment context the agent must see before the question itself (WHA-3336). */
function buildMessageWithAttachments(
  message: string,
  documents: Array<{ id: number | string; filename: string }>,
  issues: Array<{ id: number | string; issue_number: number | string; title: string }>
): string {
  if (documents.length === 0 && issues.length === 0) return message;

  let header = "";
  if (documents.length > 0) {
    const docList = documents.map((d) => `- ${d.filename} (document_id: ${d.id})`).join("\n");
    header += `[ATTACHED DOCUMENTS - The user attached the following file(s) to their query:]\n${docList}\n\n`;
  }
  if (issues.length > 0) {
    const issueList = issues
      .map((i) => `- Issue #${i.issue_number}: ${i.title} (issue_id: ${i.id})`)
      .join("\n");
    header += `[ATTACHED ISSUES - The user attached the following issue(s) to their query:]\n${issueList}\n\n`;
  }
  return `${header}User question: ${message}`;
}
